import {
  Center,
  Divider,
  Fieldset,
  Grid,
  PasswordInput,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'

import { useState } from 'react'
import { z } from 'zod'
import type { typeToFlattenedError } from 'zod'
import { FormLabel } from '~/components/form-label'
import { PREFECTURES } from '~/core/shared/constants'
import { VALIDATION_MESSAGES } from '~/core/shared/messages'
import { genYears } from '~/core/shared/utils/misc'
import { passwordValidationRegex } from '~/core/shared/utils/misc'

// MEMO: signupFormはstudentFormにメールアドレス・パスワードの項目を追加したもの
// もう一つの違いとして、idがまだわからないためI/Oもバリデーションロジックも変わる
// studentFormは学生情報のフォームで、学部・学科・学年などの情報を入力するフォーム

const PROJECT_YEAR = Number.parseInt(import.meta.env.VITE_PROJECT_YEAR)

const DEFAULT_AGE = 23

export const signupFormSchema = z
  .object({
    email: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_EMAIL })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_EMAIL })
      .max(1024, { message: VALIDATION_MESSAGES.MAX_LENGTH_EMAIL })
      .email({ message: VALIDATION_MESSAGES.INVALID_EMAIL }),
    password: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_PASSWORD })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_PASSWORD })
      .min(8, { message: VALIDATION_MESSAGES.MIN_LENGTH_PASSWORD })
      .max(64, { message: VALIDATION_MESSAGES.MAX_LENGTH_PASSWORD })
      .regex(passwordValidationRegex, {
        message: VALIDATION_MESSAGES.CHARACTER_PASSWORD,
      }),
    passwordConfirmation: z
      .string({
        required_error: VALIDATION_MESSAGES.EMPTY_PASSWORD_CONFIRMATION,
      })
      .min(8, { message: VALIDATION_MESSAGES.MIN_LENGTH_PASSWORD_CONFIRMATION })
      .max(64, {
        message: VALIDATION_MESSAGES.MAX_LENGTH_PASSWORD_CONFIRMATION,
      })
      .regex(passwordValidationRegex, {
        message: VALIDATION_MESSAGES.CHARACTER_PASSWORD_CONFIRMATION,
      }),
    familyName: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_FAMILY_NAME })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_FAMILY_NAME })
      .max(100, { message: VALIDATION_MESSAGES.MAX_LENGTH_FAMILY_NAME }),
    givenName: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_GIVEN_NAME })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_GIVEN_NAME })
      .max(100, { message: VALIDATION_MESSAGES.MAX_LENGTH_GIVEN_NAME }),

    familyNameKana: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_FAMILY_NAME_KANA })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_FAMILY_NAME_KANA })
      .max(100, { message: VALIDATION_MESSAGES.MAX_FAMILY_NAME_KANA })
      .regex(/^[ァ-ヶー]+$/, {
        message: VALIDATION_MESSAGES.KANA_ONLY_FAMILY_NAME_KANA,
      }),
    givenNameKana: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_GIVEN_NAME_KANA })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_GIVEN_NAME_KANA })
      .max(100, { message: VALIDATION_MESSAGES.MAX_GIVEN_NAME_KANA })
      .regex(/^[ァ-ヶー]+$/, {
        message: VALIDATION_MESSAGES.KANA_ONLY_GIVEN_NAME_KANA,
      }),

    sex: z.string({ required_error: VALIDATION_MESSAGES.EMPTY_SEX }),

    birthDateYear: z.preprocess(
      (val) => Number(val),
      z.number({
        required_error: VALIDATION_MESSAGES.EMPTY_BIRTH_YEAR,
      }),
    ),
    birthDateMonth: z.preprocess(
      (val) => Number(val),
      z.number({
        required_error: VALIDATION_MESSAGES.EMPTY_BIRTH_MONTH,
      }),
    ),
    birthDateDay: z.preprocess(
      (val) => Number(val),
      z.number({
        required_error: VALIDATION_MESSAGES.EMPTY_BIRTH_DAY,
      }),
    ),

    tel: z.preprocess(
      (item) => (typeof item !== 'string' ? '' : item.replace(/-/g, '')),
      z
        .string({ required_error: VALIDATION_MESSAGES.EMPTY_TEL })
        .min(1, { message: VALIDATION_MESSAGES.EMPTY_TEL })
        .regex(/^\d+$/, { message: VALIDATION_MESSAGES.ONLY_NUM_TEL })
        .length(11, { message: VALIDATION_MESSAGES.PHONE_LENGTH_TEL }),
    ),

    postalCode: z.preprocess(
      (item) => (typeof item !== 'string' ? '' : item.replace(/-/g, '')),
      z
        .string()
        .regex(/^\d*$/, { message: VALIDATION_MESSAGES.ONLY_NUM_POSTAL_CODE })
        .max(7, { message: VALIDATION_MESSAGES.MAX_LENGTH_POSTAL_CODE }),
    ),

    prefecture: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_PREFECTURE })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_PREFECTURE }),
    city: z
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_CITY })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_CITY })
      .max(200, { message: VALIDATION_MESSAGES.MAX_LENGTH_CITY }),
    buildingName: z
      .union([
        z.literal(''),
        z
          .string()
          .max(200, { message: VALIDATION_MESSAGES.MAX_LENGTH_BUILDING_NAME }),
      ])
      .nullable(),
    universityEmail: z
      .union([
        z.literal(''),
        z
          .string()
          .email({ message: VALIDATION_MESSAGES.INVALID_EMAIL })
          .max(1024, { message: VALIDATION_MESSAGES.MAX_LENGTH_EMAIL }),
      ])
      .nullable(),

    academicBackground: z.enum(['学部卒', '大学院卒']), // 最終学歴
    university: z // 大学名
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_UNIVERSITY })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_UNIVERSITY })
      .max(100, { message: VALIDATION_MESSAGES.MAX_LENGTH_UNIVERSITY }),
    faculty: z // 学部
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_FACULTY })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_FACULTY })
      .max(100, { message: VALIDATION_MESSAGES.MAX_LENGTH_FACULTY }),
    department: z // 学科
      .string({ required_error: VALIDATION_MESSAGES.EMPTY_DEPARTMENT })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_DEPARTMENT })
      .max(100, { message: VALIDATION_MESSAGES.MAX_LENGTH_DEPARTMENT }),
    graduate: z // 大学院名
      .string()
      .max(100, { message: VALIDATION_MESSAGES.MAX_LENGTH_GRADUATE_NAME })
      .optional()
      .nullable(),
    graduateFaculty: z // 大学院 学部名
      .string()
      .max(100, {
        message: VALIDATION_MESSAGES.MAX_LENGTH_GRADUATE_FACULTY_NAME,
      })
      .optional()
      .nullable(),
    graduateDepartment: z // 大学院 学科名
      .string()
      .max(100, {
        message: VALIDATION_MESSAGES.MAX_LENGTH_GRADUATE_DEPARTMENT_NAME,
      })
      .optional()
      .nullable(),
    academicTrack: z // 学制
      .enum(['文系', '理系'], {
        message: VALIDATION_MESSAGES.EMPTY_ACADEMIC_TRACK,
      }),
    universityPrefecture: z // 大学都道府県
      .string({
        required_error: VALIDATION_MESSAGES.EMPTY_UNIVERSITY_PREFECTURE,
      })
      .min(1, { message: VALIDATION_MESSAGES.EMPTY_UNIVERSITY_PREFECTURE }),

    graduateYear: z.preprocess(
      // 卒業年度
      (val) => Number(val),
      z
        .number({
          required_error: VALIDATION_MESSAGES.EMPTY_GRADUATE_YEAR,
        })
        .min(1, { message: VALIDATION_MESSAGES.EMPTY_GRADUATE_YEAR }),
    ),
    graduateMonth: z // 卒業月
      .enum(['3', '9'])
      .transform((val) => Number(val)),
  })
  .refine(
    ({ password, passwordConfirmation }) => password === passwordConfirmation,
    {
      message: VALIDATION_MESSAGES.UN_MATCH_PASSWORD,
      path: ['passwordConfirmation'],
    },
  )
  // 都道府県で海外を選択時には郵便番号の空白チェックを行わない
  .superRefine((data, ctx) => {
    if (data.prefecture === '海外') return

    if (!data.postalCode || data.postalCode === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: VALIDATION_MESSAGES.EMPTY_POSTAL_CODE,
        path: ['postalCode'],
      })
    }
  })
  // 大学院卒選択時には大学院用のバリデーションを行う
  .superRefine((data, ctx) => {
    if (data.academicBackground !== '大学院卒') return

    if (!data.graduate || data.graduate.trim() === '')
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '大学院名を入力してください',
        path: ['graduate'],
      })
    if (!data.graduateFaculty || data.graduateFaculty.trim() === '')
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '大学院 専攻名を入力してください',
        path: ['graduateFaculty'],
      })
  })

type InitialValues = {
  familyName: string | null
  givenName: string | null
  familyNameKana: string | null
  givenNameKana: string | null
  sex: string | null
  birthDateYear: number | null
  birthDateMonth: number | null
  birthDateDay: number | null
  tel: string | null
  postalCode: string | null
  prefecture: string | null
  city: string | null
  buildingName: string | null
  universityEmail: string | null
  academicBackground: string | null
  university: string | null
  faculty: string | null
  department: string | null
  academicTrack: string | null
  universityPrefecture: string | null
  graduateYear: number | null
  graduateMonth: number | null
  graduate: string | null
  graduateFaculty: string | null
  graduateDepartment: string | null
}

type SignupFormProps = {
  validationErrors?: typeToFlattenedError<z.infer<typeof signupFormSchema>>
  applicationError?: string
  initialValues?: InitialValues
}

export const SignupForm = ({
  validationErrors,
  applicationError,
  initialValues,
}: SignupFormProps) => {
  const [isGraduate, setIsGraduate] = useState<boolean>(
    initialValues?.academicBackground === '大学院卒',
  )
  return (
    <Stack gap='md' w='100%' h='100%'>
      <Fieldset
        legend={<FormLabel title='メールアドレスを設定してください' require />}
        variant='unstyled'
      >
        <Stack>
          <TextInput
            size='md'
            name='email'
            label={<Text fw='bold'>メールアドレス</Text>}
            placeholder='新しいメールアドレス'
            description='※Gmailが推奨となっています'
            inputWrapperOrder={['label', 'input', 'error', 'description']}
            error={validationErrors?.fieldErrors.email?.at(0)}
          />
          <PasswordInput
            size='md'
            name='password'
            label={<Text fw='bold'>パスワード</Text>}
            placeholder='パスワード(8文字〜64文字)'
            description='※8文字以上・64文字以内、数字を含む大文字／小文字の英字が含まれている必要があります'
            inputWrapperOrder={['label', 'input', 'error', 'description']}
            error={validationErrors?.fieldErrors.password?.at(0)}
          />
          <PasswordInput
            size='md'
            name='passwordConfirmation'
            label={<Text fw='bold'>パスワード（確認）</Text>}
            placeholder='もう一度同じパスワードを入力してください'
            error={validationErrors?.fieldErrors.passwordConfirmation?.at(0)}
          />
        </Stack>
      </Fieldset>

      <Fieldset
        legend={<FormLabel title='お名前を教えてください' require />}
        variant='unstyled'
      >
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              name='familyName'
              label={<Text fw='bold'>姓</Text>}
              size='md'
              placeholder='山田'
              defaultValue={initialValues?.familyName ?? ''}
              inputWrapperOrder={['label', 'input', 'error']}
              error={validationErrors?.fieldErrors?.familyName?.at(0)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              name='givenName'
              label={<Text fw='bold'>名</Text>}
              size='md'
              placeholder='太郎'
              defaultValue={initialValues?.givenName ?? ''}
              inputWrapperOrder={['label', 'input', 'error']}
              error={validationErrors?.fieldErrors.givenName?.at(0)}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              name='familyNameKana'
              label={<Text fw='bold'>セイ</Text>}
              size='md'
              placeholder='ヤマダ'
              defaultValue={initialValues?.familyNameKana ?? ''}
              inputWrapperOrder={['label', 'input', 'error']}
              error={validationErrors?.fieldErrors.familyNameKana?.at(0)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              name='givenNameKana'
              label={<Text fw='bold'>メイ</Text>}
              size='md'
              placeholder='タロウ'
              defaultValue={initialValues?.givenNameKana ?? ''}
              inputWrapperOrder={['label', 'input', 'error']}
              error={validationErrors?.fieldErrors.givenNameKana?.at(0)}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>

      <Fieldset
        legend={<FormLabel title='性別を教えてください' require />}
        variant='unstyled'
      >
        <Stack>
          <Radio
            name='sex'
            label={<Text fw='bold'>男性</Text>}
            defaultChecked={initialValues?.sex === '男性'}
            value='男性'
          />
          <Radio
            name='sex'
            label={<Text fw='bold'>女性</Text>}
            defaultChecked={initialValues?.sex === '女性'}
            value='女性'
          />
        </Stack>
      </Fieldset>

      <Fieldset
        legend={<FormLabel title='生年月日を教えてください' require />}
        variant='unstyled'
      >
        <Grid>
          <Grid.Col span={12}>
            <Grid align='flex-end'>
              <Grid.Col span={'auto'}>
                <Select
                  name='birthDateYear'
                  size='md'
                  data={genYears(10, 5, PROJECT_YEAR - DEFAULT_AGE).map(
                    (year) => year.toString(),
                  )}
                  defaultValue={(
                    initialValues?.birthDateYear ?? PROJECT_YEAR - DEFAULT_AGE
                  ).toString()}
                  error={validationErrors?.fieldErrors.birthDateYear?.at(0)}
                />
              </Grid.Col>
              <Grid.Col span={0.5}>
                <Text ta='right' fw='bold'>
                  年
                </Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={6}>
            <Grid align='flex-end'>
              <Grid.Col span={'auto'}>
                <Select
                  name='birthDateMonth'
                  size='md'
                  data={[
                    ...Array.from(Array(12).keys()).map((i) =>
                      (i + 1).toString(),
                    ),
                  ]}
                  defaultValue={
                    initialValues?.birthDateMonth
                      ? initialValues.birthDateMonth.toString()
                      : '1'
                  }
                  error={validationErrors?.fieldErrors.birthDateMonth?.at(0)}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <Text ta='right' fw='bold'>
                  月
                </Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={6}>
            <Grid align='flex-end'>
              <Grid.Col span={'auto'}>
                <Select
                  name='birthDateDay'
                  size='md'
                  data={[
                    ...Array.from(Array(31).keys()).map((i) =>
                      (i + 1).toString(),
                    ),
                  ]}
                  defaultValue={
                    initialValues?.birthDateDay
                      ? initialValues.birthDateDay.toString()
                      : '1'
                  }
                  error={validationErrors?.fieldErrors.birthDateDay?.at(0)}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <Text ta='right' fw='bold'>
                  日
                </Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      </Fieldset>

      <Fieldset
        legend={<FormLabel title='電話番号を教えてください' require />}
        variant='unstyled'
      >
        <TextInput
          name='tel'
          placeholder='09012345678'
          size='md'
          defaultValue={initialValues?.tel ?? ''}
          inputWrapperOrder={['input', 'error', 'description']}
          description='※携帯電話の番号をご入力ください。ハイフンなし、半角英数字で入力してください。お電話がつながらない場合、SMSでメッセージをお送りする場合がございます。'
          error={validationErrors?.fieldErrors.tel?.at(0)}
        />
      </Fieldset>

      <Fieldset
        legend={<FormLabel title='現住所を教えてください' require />}
        variant='unstyled'
      >
        <Stack>
          <TextInput
            name='postalCode'
            label={<Text fw='bold'>郵便番号</Text>}
            placeholder='0001234'
            size='md'
            defaultValue={initialValues?.postalCode ?? ''}
            inputWrapperOrder={['label', 'input', 'error', 'description']}
            description='※ハイフンなし、半角英数字で入力してください。'
            error={validationErrors?.fieldErrors.postalCode?.at(0)}
          />
          <Select
            name='prefecture'
            label={<Text fw='bold'>都道府県</Text>}
            data={[...PREFECTURES, '海外']}
            defaultValue={initialValues?.prefecture ?? PREFECTURES[12]}
            size='md'
            error={validationErrors?.fieldErrors.prefecture?.at(0)}
          />
          <TextInput
            name='city'
            label={<Text fw='bold'>市区郡・地名・番地</Text>}
            placeholder='千代田区丸の内1-1'
            size='md'
            inputWrapperOrder={['label', 'input', 'error', 'description']}
            description='※半角英数字で入力してください。'
            defaultValue={initialValues?.city ?? ''}
            error={validationErrors?.fieldErrors.city?.at(0)}
          />
          <TextInput
            name='buildingName'
            label={<Text fw='bold'>アパート・マンション名・部屋番号</Text>}
            placeholder='シンアドマンション101'
            size='md'
            inputWrapperOrder={['label', 'input', 'error', 'description']}
            description='※半角英数字で入力してください。'
            defaultValue={initialValues?.buildingName ?? ''}
            error={validationErrors?.fieldErrors.buildingName?.at(0)}
          />
        </Stack>
      </Fieldset>

      <Fieldset
        legend={
          <FormLabel
            title='大学のメールアドレスを教えてください'
            require={false}
          />
        }
        variant='unstyled'
      >
        <Stack>
          <TextInput
            name='universityEmail'
            size='md'
            label={<Text fw='bold'>大学メールアドレス(任意)</Text>}
            placeholder='syn-ad@example.ac.jp'
            inputWrapperOrder={['label', 'input', 'error']}
            description='※大学のメールアドレス(◯◯.ac.jp)を入力してください'
            defaultValue={initialValues?.universityEmail ?? ''}
            error={validationErrors?.fieldErrors.universityEmail?.at(0)}
          />
        </Stack>
      </Fieldset>

      <Fieldset
        legend={
          <FormLabel title='最終学歴の区分について教えてください' require />
        }
        variant='unstyled'
      >
        <Stack>
          <Radio
            name='academicBackground'
            label={<Text fw='bold'>学部卒</Text>}
            defaultChecked={!isGraduate}
            value='学部卒'
            onChange={() => setIsGraduate(false)}
          />
          <Radio
            name='academicBackground'
            label={<Text fw='bold'>大学院卒</Text>}
            onChange={() => setIsGraduate(true)}
            value='大学院卒'
            defaultChecked={isGraduate}
            error={validationErrors?.fieldErrors.academicBackground?.at(0)}
          />
        </Stack>
      </Fieldset>

      <Fieldset
        legend={
          <FormLabel
            title='大学・学部を教えてください'
            description='※大学名, 学部名は必ず正式名称で入力してください'
            require
          />
        }
        variant='unstyled'
      >
        <Stack>
          {isGraduate && (
            <>
              <TextInput
                name='graduate'
                label={<Text fw='bold'>大学院名</Text>}
                placeholder='シンアド大学大学院'
                size='md'
                defaultValue={initialValues?.graduate ?? ''}
                error={validationErrors?.fieldErrors.graduate?.at(0)}
              />
              <TextInput
                name='graduateFaculty'
                placeholder='文学専攻'
                size='md'
                label={<Text fw='bold'>大学院 専攻名</Text>}
                defaultValue={initialValues?.graduateFaculty ?? ''}
                error={validationErrors?.fieldErrors.graduateFaculty?.at(0)}
              />
              <TextInput
                name='graduateDepartment'
                label={<Text fw='bold'>大学院 コース名</Text>}
                placeholder='英文学コース'
                size='md'
                defaultValue={initialValues?.graduateDepartment ?? ''}
                error={validationErrors?.fieldErrors.graduateDepartment?.at(0)}
              />
              <Divider />
            </>
          )}
          <TextInput
            name='university'
            label={
              <Text fw='bold'>{isGraduate ? '出身大学名' : '大学名'}</Text>
            }
            placeholder='シンアド就活大学'
            size='md'
            inputWrapperOrder={['label', 'input', 'error']}
            defaultValue={initialValues?.university ?? ''}
            error={validationErrors?.fieldErrors.university?.at(0)}
          />
          <TextInput
            name='faculty'
            label={
              <Text fw='bold'>{isGraduate ? '出身大学 学部名' : '学部名'}</Text>
            }
            placeholder='文学部'
            size='md'
            inputWrapperOrder={['label', 'input', 'error']}
            defaultValue={initialValues?.faculty ?? ''}
            error={validationErrors?.fieldErrors.faculty?.at(0)}
          />
          <TextInput
            name='department'
            label={
              <Text fw='bold'>{isGraduate ? '出身大学 学科名' : '学科名'}</Text>
            }
            placeholder='英文学科'
            size='md'
            defaultValue={initialValues?.department ?? ''}
            inputWrapperOrder={['label', 'input', 'error']}
            error={validationErrors?.fieldErrors.department?.at(0)}
          />
          <Stack>
            <Radio
              name='academicTrack'
              label={<Text fw='bold'>文系</Text>}
              value='文系'
              defaultChecked={initialValues?.academicTrack === '文系'}
              error={!!validationErrors?.fieldErrors.academicTrack?.at(0)}
            />
            <Radio
              name='academicTrack'
              label={<Text fw='bold'>理系</Text>}
              value='理系'
              defaultChecked={initialValues?.academicTrack === '理系'}
              error={validationErrors?.fieldErrors.academicTrack?.at(0)}
            />
          </Stack>
        </Stack>
      </Fieldset>

      <Fieldset
        legend={<FormLabel title='大学の都道府県を教えてください' require />}
        variant='unstyled'
      >
        <Stack>
          <Select
            name='universityPrefecture'
            data={[...PREFECTURES, '海外']}
            defaultValue={
              initialValues?.universityPrefecture ?? PREFECTURES[12]
            }
            error={validationErrors?.fieldErrors.universityPrefecture?.at(0)}
          />
        </Stack>
      </Fieldset>

      <Fieldset
        legend={<FormLabel title='卒業年月を教えてください' require />}
        variant='unstyled'
      >
        <Grid>
          <Grid.Col span={6}>
            <Grid align='flex-end'>
              <Grid.Col span={'auto'}>
                <Select
                  name='graduateYear'
                  data={genYears(3, 3, PROJECT_YEAR).map((year) =>
                    year.toString(),
                  )}
                  defaultValue={(
                    initialValues?.graduateYear ?? PROJECT_YEAR
                  ).toString()}
                  error={validationErrors?.fieldErrors.graduateYear?.at(0)}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <Text ta='right' fw='bold'>
                  年
                </Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={6}>
            <Grid align='flex-end'>
              <Grid.Col span={'auto'}>
                <Select
                  name='graduateMonth'
                  data={['3', '9']}
                  defaultValue={
                    initialValues?.graduateMonth
                      ? initialValues.graduateMonth.toString()
                      : '3'
                  }
                  error={validationErrors?.fieldErrors.graduateMonth?.at(0)}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <Text ta='right' fw='bold'>
                  月
                </Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      </Fieldset>
      {applicationError && (
        <Center>
          <Text c='error.4'>{applicationError}</Text>
        </Center>
      )}
    </Stack>
  )
}
